import { getRouteApi, Link } from '@tanstack/react-router'
import { BookOpenText, ChevronRight, LayoutGrid, MapPin, Workflow, type LucideIcon } from 'lucide-react'
import { z } from 'zod'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { AiHealthCard } from '@/features/settings/AiHealthCard'
import { AiProviderSettings } from '@/features/settings/AiProviderSettings'
import { AntigravitySettings } from '@/features/settings/AntigravitySettings'
import { BudgetsForm } from '@/features/settings/BudgetsForm'
import { CacheStatsCard } from '@/features/settings/CacheStatsCard'
import { CostSummary } from '@/features/settings/CostSummary'
import { ExclusionsForm } from '@/features/settings/ExclusionsForm'
import { StorySettingsForm } from '@/features/settings/StorySettingsForm'
import { TtsSettings } from '@/features/settings/TtsSettings'

/**
 * `/settings?tab=` — story defaults, AI, spending, the picture library, and **Advanced**
 * (Activity = the jobs console, Vocabulary, the stage editor): the expert tools that left the nav
 * (docs/SKEL-STORY.md P8).
 */
const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined)

export const SETTINGS_TABS = ['story', 'ai', 'budgets', 'costs', 'index', 'cache', 'advanced'] as const
export type SettingsTab = (typeof SETTINGS_TABS)[number]

export const settingsSearchSchema = z.object({
  tab: lenient(z.enum(SETTINGS_TABS)),
})

export type SettingsSearch = z.infer<typeof settingsSearchSchema>

export function validateSettingsSearch(input: Record<string, unknown>): SettingsSearch {
  return settingsSearchSchema.parse(input)
}

const TAB_LABEL: Record<SettingsTab, string> = {
  story: 'Stories',
  ai: 'AI',
  budgets: 'Spending',
  costs: 'AI usage',
  index: 'Picture library',
  cache: 'Picture cache',
  advanced: 'Advanced',
}

const route = getRouteApi('/settings')

/**
 * One page, six tabs, each a self-contained card that loads its own data.
 *
 * The tab lives in the URL so "open Settings → Stories" is a link somebody
 * can send, and so a failed save can be reopened exactly where it was.
 */
export function SettingsIndexPage() {
  const search = route.useSearch()
  const navigate = route.useNavigate()
  const tab = search.tab ?? 'story'

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-5 px-4 py-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ink">Settings</h1>
          <p className="text-sm text-ink-2">What a story sounds like, AI models and connections, and the tools for experts.</p>
        </div>
      </header>

      <Tabs value={tab} onValueChange={(v) => void navigate({ search: { tab: v as SettingsTab }, replace: true })}>
        <TabsList className="h-auto flex-wrap">
          {SETTINGS_TABS.map((t) => (
            <TabsTrigger key={t} value={t}>
              {TAB_LABEL[t]}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="ai" className="space-y-4 pt-4">
          <AiProviderSettings />
          <AiHealthCard />
          <AntigravitySettings />
        </TabsContent>
        <TabsContent value="budgets" className="pt-4">
          <BudgetsForm />
        </TabsContent>
        <TabsContent value="index" className="pt-4">
          <ExclusionsForm />
        </TabsContent>
        <TabsContent value="cache" className="pt-4">
          <CacheStatsCard />
        </TabsContent>
        <TabsContent value="costs" className="pt-4">
          <CostSummary />
        </TabsContent>
        <TabsContent value="story" className="space-y-4 pt-4">
          <TtsSettings />
          <StorySettingsForm />
        </TabsContent>
        <TabsContent value="advanced" className="pt-4">
          <AdvancedTools />
        </TabsContent>
      </Tabs>
    </div>
  )
}

interface Tool {
  to: '/jobs' | '/settings/vocabulary' | '/library' | '/sprites'
  search?: { kind?: 'background' }
  icon: LucideIcon
  title: string
  hint: string
}

const TOOLS: Tool[] = [
  { to: '/sprites', icon: LayoutGrid, title: 'Sprite editor', hint: 'Prepare character animation clips for stories made with sprite sheets.' },
  { to: '/jobs', icon: Workflow, title: 'Activity', hint: 'Every background job — what ran, what is waiting, what failed — with retry and cancel.' },
  { to: '/settings/vocabulary', icon: BookOpenText, title: 'Vocabulary', hint: 'The words the studio understands: kinds of places, themes, actions, blocked words.' },
  { to: '/library', search: { kind: 'background' }, icon: MapPin, title: 'Places', hint: 'Fix where characters can stand in a background (the stage editor).' },
  { to: '/library', icon: LayoutGrid, title: 'Picture library', hint: 'Every picture the studio has indexed, with its details.' },
]

/** Settings › Advanced: links to the expert tools that are no longer in the nav. */
export function AdvancedTools() {
  return (
    <section className="card flex flex-col gap-1 p-2" aria-label="Advanced tools" data-testid="settings-advanced">
      {TOOLS.map((t) => {
        const Icon = t.icon
        return (
          <Link key={t.title} to={t.to} search={t.search ?? {}} className="flex min-h-16 items-center gap-4 rounded-xl px-3 py-2 text-ink transition-colors hover:bg-paper-2 focus-visible:focus-ring">
            <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-paper-2 text-ink-2">
              <Icon className="size-5" aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-medium">{t.title}</span>
              <span className="block text-sm text-ink-2">{t.hint}</span>
            </span>
            <ChevronRight className="size-4 shrink-0 text-ink-3" aria-hidden />
          </Link>
        )
      })}
    </section>
  )
}
